import React, { useState, useEffect } from 'react';
import { getDetalleHorasContrato, DetalleContratoResponse } from '../services/horasService';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

interface DetalleHorasContratoProps {
  contrato: string;
  fechaInicio?: Date;
  fechaFin?: Date;
  cliente?: string;
  horasContratadas?: number;
  onClose: () => void;
}

const DetalleHorasContrato: React.FC<DetalleHorasContratoProps> = ({ contrato, fechaInicio, fechaFin, cliente, horasContratadas, onClose }) => {
  const [detalleData, setDetalleData] = useState<DetalleContratoResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetalle = async () => {
      try {
        setLoading(true);
        const data = await getDetalleHorasContrato(contrato, fechaInicio, fechaFin, cliente);
        setDetalleData(data);
        setError(null);
      } catch (err) {
        setError('Error al cargar el detalle de horas');
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDetalle();
  }, [contrato, cliente, fechaInicio, fechaFin]);

  const detalleHoras = detalleData?.detalleHoras || [];
  const horasContratadasBackend = detalleData?.horasContratadas || 0;
  const periodoFiltrado = detalleData?.periodoFiltrado || 'Filtrado por vigencia del contrato';
  const totalHoras = detalleHoras.reduce((sum, item) => sum + item.horas, 0);

  // Debug: Log para verificar las fechas
  console.log('DetalleHorasContrato - detalleData:', detalleData);
  console.log('DetalleHorasContrato - fechaInicio:', detalleData?.fechaInicio);
  console.log('DetalleHorasContrato - fechaFin:', detalleData?.fechaFin);

  // Ordenar por día y número de ticket
  const detalleHorasOrdenado = [...detalleHoras].sort((a, b) => {
    const fechaA = new Date(a.fecha);
    const fechaB = new Date(b.fecha);
    
    if (fechaA.getTime() !== fechaB.getTime()) {
      return fechaA.getTime() - fechaB.getTime();
    }
    
    // Si las fechas son iguales, ordenar por número de ticket
    return a.numeroTicket.toString().localeCompare(b.numeroTicket.toString());
  });

  // Función para formatear fecha como DD/MM/AAAA
  const formatearFecha = (fechaString: string) => {
    const fecha = new Date(fechaString);
    return fecha.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };



  const exportToExcel = () => {
    // Información del encabezado
    const headerInfo = [
      ['Detalle de Horas Aplicadas'],
      [''],
      [`Contrato: ${contrato}`],
      cliente ? [`Cliente: ${cliente}`] : [''],
      [''],
      [`Generado el: ${new Date().toLocaleDateString('es-AR')} - ${new Date().toLocaleTimeString('es-AR')}`],
      [''],
    ];

    // Información de vigencia
    if (detalleData?.fechaInicio && detalleData?.fechaFin) {
      const fechaInicio = new Date(detalleData.fechaInicio).toLocaleDateString('es-AR');
      const fechaFin = new Date(detalleData.fechaFin).toLocaleDateString('es-AR');
      headerInfo.push([`Vigencia: ${fechaInicio} - ${fechaFin}`]);
    }
    headerInfo.push(['']);

    // Información de horas
    headerInfo.push(
      [`Horas Aplicadas: ${totalHoras.toFixed(2)}h`],
      [`Horas Contratadas: ${(horasContratadas || horasContratadasBackend).toFixed(2)}h`],
      [`Utilización: ${((horasContratadas || horasContratadasBackend) > 0 ? ((totalHoras / (horasContratadas || horasContratadasBackend)) * 100).toFixed(1) : '0.0')}%`],
      ['']
    );

    // Encabezados de la tabla
    const headers = ['Fecha', 'Número Ticket', 'Título', 'Horas', 'Estado', 'Categoría', 'Solicitante'];
    
    // Datos de la tabla
    const data = detalleHorasOrdenado.map(item => [
      formatearFecha(item.fecha),
      item.numeroTicket,
      item.titulo,
      item.horas,
      item.estadoTicket,
      item.categoriaTicket,
      item.solicitante
    ]);

    // Resumen final
    const summary = [
      [''],
      [`Total de registros: ${detalleHoras.length}`],
      [`Total de horas aplicadas: ${totalHoras.toFixed(2)}h`],
      [`Total de horas contratadas: ${(horasContratadas || horasContratadasBackend).toFixed(2)}h`],
      [`Utilización: ${((horasContratadas || horasContratadasBackend) > 0 ? ((totalHoras / (horasContratadas || horasContratadasBackend)) * 100).toFixed(1) : '0.0')}%`]
    ];

    // Combinar toda la información
    const allData = [...headerInfo, headers, ...data, ...summary];
    
    const ws = XLSX.utils.aoa_to_sheet(allData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'DetalleHoras');
    const fileName = `detalle_horas_${contrato.replace(/[^a-zA-Z0-9]/g, '_')}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Configurar fuente para caracteres especiales
    doc.setFont('helvetica');
    
    // Título del documento
    doc.setFontSize(20);
    doc.setTextColor(44, 62, 80);
    doc.text('Detalle de Horas Aplicadas', 20, 30);
    
    // Información del contrato
    doc.setFontSize(14);
    doc.setTextColor(52, 73, 94);
    doc.text(`Contrato: ${contrato}`, 20, 45);
    
    if (cliente) {
      doc.text(`Cliente: ${cliente}`, 20, 55);
    }
    
    // Fecha de generación
    const fechaActual = new Date().toLocaleDateString('es-AR');
    const horaActual = new Date().toLocaleTimeString('es-AR');
    doc.setFontSize(10);
    doc.setTextColor(149, 165, 166);
    doc.text(`Generado el: ${fechaActual} - ${horaActual}`, 20, 65);
    
    // Información de vigencia
    if (detalleData?.fechaInicio && detalleData?.fechaFin) {
      const fechaInicio = new Date(detalleData.fechaInicio).toLocaleDateString('es-AR');
      const fechaFin = new Date(detalleData.fechaFin).toLocaleDateString('es-AR');
      doc.setFontSize(12);
      doc.setTextColor(44, 62, 80);
      doc.text(`Vigencia: ${fechaInicio} - ${fechaFin}`, 20, 75);
    }
        
    // Información de horas
    doc.setFontSize(12);
    doc.setTextColor(44, 62, 80);
    doc.text(`Horas Aplicadas: ${totalHoras.toFixed(2)}h`, 20, 85);
    doc.text(`Horas Contratadas: ${(horasContratadas || horasContratadasBackend).toFixed(2)}h`, 20, 95);
    doc.text(`Utilización: ${((horasContratadas || horasContratadasBackend) > 0 ? ((totalHoras / (horasContratadas || horasContratadasBackend)) * 100).toFixed(1) : '0.0')}%`, 20, 105);
    
    // Preparar datos para la tabla
    const tableData = detalleHorasOrdenado.map(item => [
      formatearFecha(item.fecha),
      item.numeroTicket.toString(),
      item.titulo,
      `${item.horas.toFixed(2)}h`,
      item.estadoTicket || 'Sin Estado',
      item.categoriaTicket,
      item.solicitante
    ]);
    
    // Agregar tabla
    autoTable(doc, {
      head: [['Fecha', 'N° Ticket', 'Título', 'Horas', 'Estado', 'Categoría', 'Solicitante']],
      body: tableData,
      startY: 115,
      styles: {
        fontSize: 8,
        cellPadding: 2,
        lineColor: [44, 62, 80],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [52, 73, 94],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9,
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250],
      },
      columnStyles: {
        0: { cellWidth: 20 }, // Fecha
        1: { cellWidth: 20 }, // N° Ticket
        2: { cellWidth: 35 }, // Título
        3: { cellWidth: 18 }, // Horas
        4: { cellWidth: 22 }, // Estado
        5: { cellWidth: 25 }, // Categoría
        6: { cellWidth: 25 }, // Solicitante
      },
      didDrawPage: function (data) {
        // Agregar pie de página
        const pageCount = doc.getNumberOfPages();
        doc.setFontSize(8);
        doc.setTextColor(149, 165, 166);
        doc.text(`Página ${pageCount}`, data.settings.margin.left, doc.internal.pageSize.height - 10);
      },
    });
    
    // Agregar resumen al final 
    const finalY = (doc as any).lastAutoTable.finalY || 115;
    doc.setFontSize(12);
    doc.setTextColor(44, 62, 80);
    doc.text(`Total de registros: ${detalleHoras.length}`, 20, finalY + 15);

    // Descargar el PDF
    const fileName = `detalle_horas_${contrato.replace(/[^a-zA-Z0-9]/g, '_')}_${fechaActual.replace(/\//g, '-')}.pdf`;
    doc.save(fileName);
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="loading">Cargando detalle de horas...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="error">{error}</div>
          <button className="close-button" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content detalle-horas">
        <div className="modal-header">
          <h2>📋 Detalle de Horas Aplicadas</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          <div className="contrato-info">
            <h3>Contrato: {contrato}</h3>
            {cliente && <p>Cliente: {cliente}</p>}
            <p><strong>Período:</strong> {periodoFiltrado}</p>
            <div className="horas-item">
              <span className="horas-label">Vigencia:</span>
              <span className="horas-valor vigencia">
                {detalleData?.fechaInicio ? 
                  new Date(detalleData.fechaInicio).toLocaleDateString('es-AR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  }) : 'N/A'} - {detalleData?.fechaFin ? 
                  new Date(detalleData.fechaFin).toLocaleDateString('es-AR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  }) : 'N/A'}
              </span>
            </div>
            <div className="horas-info">
              <div className="horas-item">
                <span className="horas-label">Horas Aplicadas:</span>
                <span className="horas-valor aplicadas">{totalHoras.toFixed(2)}h</span>
              </div>
              <div className="horas-item">
                <span className="horas-label">Horas Contratadas:</span>
                <span className="horas-valor contratadas">{(horasContratadas || horasContratadasBackend).toFixed(2)}h</span>
              </div>
              <div className="horas-item">
                <span className="horas-label">Utilización:</span>
                <span className="horas-valor utilizacion">
                  {((horasContratadas || horasContratadasBackend) > 0 ? ((totalHoras / (horasContratadas || horasContratadasBackend)) * 100).toFixed(1) : '0.0')}%
                </span>
              </div>
            </div>
          </div>

          <div className="actions">
            <button className="export-button" onClick={exportToExcel}>
              📊 Exportar a Excel
            </button>
            <button className="export-button pdf-button" onClick={exportToPDF}>
              🖨️ Exportar a PDF
            </button>
          </div>

          <div className="detalle-table-container">
            <table className="detalle-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>N° Ticket</th>
                  <th>Título</th>
                  <th>Horas</th>
                  <th>Estado</th>
                  <th>Categoría</th>
                  <th>Solicitante</th>
                </tr>
              </thead>
              <tbody>
                {detalleHorasOrdenado.map((item, index) => (
                  <tr key={index}>
                    <td className="fecha">{formatearFecha(item.fecha)}</td>
                    <td className="ticket-number">{item.numeroTicket}</td>
                    <td className="ticket-title">{item.titulo}</td>
                    <td className="hours">{item.horas.toFixed(2)}h</td>
                    <td className="estado" style={{ textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block',
                        background: '#f0f0f0',
                        borderRadius: '7px',
                        padding: '2px 8px',
                        fontWeight: 'bold',
                        fontSize: '0.92em',
                        minWidth: 48,
                        border: '1px solid #e0e0e0',
                      }}>
                        {item.estadoTicket || 'Sin Estado'}
                      </span>
                    </td>
                    <td className="categoria">{item.categoriaTicket}</td>
                    <td className="solicitante">{item.solicitante}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan={3}><strong>Total</strong></td>
                  <td><strong>{totalHoras.toFixed(2)}h</strong></td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {detalleHorasOrdenado.length === 0 && (
            <div className="no-data">
              <p>No se encontraron horas aplicadas para este contrato dentro del período de vigencia.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetalleHorasContrato; 